import {Component, OnInit} from '@angular/core';

import {Router} from '@angular/router';
import {SecurityConfigService} from '@twpub/shared/services/security-config.service';
import {NGXLogger} from 'ngx-logger';

@Component({
  selector: 'app-access-denied',
  standalone: true,
  imports: [],
  template: `
    <div class="access-denied-container">
      <div class="access-denied-card">
        <div class="icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
          </svg>
        </div>
        <h1>Access Denied</h1>
        <p class="message">
          You have been authenticated, but you do not have permission to access this application.
        </p>
        @if (userName) {
          <div class="user-info">
            <p>Logged in as: <strong>{{ userName }}</strong></p>
            @if (userEmail) {
              <p class="email">{{ userEmail }}</p>
            }
          </div>
        }
        <p class="help-text">
          If you believe you should have access, please contact your administrator.
        </p>
        <div class="actions">
          @if (showLogout) {
            <button class="btn-primary" (click)="logout()">Sign Out</button>
          }
          <button class="btn-secondary" (click)="tryAgain()">Try Again</button>
        </div>
      </div>
    </div>
    `,
  styles: [`
    .access-denied-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: #f5f5f5;
      padding: 20px;
    }

    .access-denied-card {
      background: white;
      border-radius: 8px;
      padding: 48px;
      max-width: 480px;
      width: 100%;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .icon {
      color: #e53e3e;
      margin-bottom: 24px;
    }

    h1 {
      color: #1a202c;
      font-size: 28px;
      font-weight: 600;
      margin: 0 0 16px 0;
    }

    .message {
      color: #4a5568;
      font-size: 16px;
      line-height: 1.6;
      margin: 0 0 24px 0;
    }

    .user-info {
      background: #f7fafc;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
    }

    .user-info p {
      margin: 0;
      color: #2d3748;
    }

    .user-info .email {
      color: #718096;
      font-size: 14px;
      margin-top: 4px;
    }

    .help-text {
      color: #718096;
      font-size: 14px;
      margin: 0 0 32px 0;
    }

    .actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
    }

    button {
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      border: none;
    }

    .btn-primary {
      background: #3182ce;
      color: white;
    }

    .btn-primary:hover {
      background: #2c5282;
    }

    .btn-secondary {
      background: #edf2f7;
      color: #4a5568;
    }

    .btn-secondary:hover {
      background: #e2e8f0;
    }
  `]
})
export class AccessDeniedComponent implements OnInit {
  userName: string | null = null;
  userEmail: string | null = null;
  showLogout = false;

  constructor(
    private logger: NGXLogger,
    private router: Router,
    private securityService: SecurityConfigService
  ) {
  }

  ngOnInit(): void {
    const user = this.securityService.getUser();
    if (user) {
      this.userName = user.name || user.preferred_username;
      this.userEmail = user.email;
    }

    const config = this.securityService.getConfig();
    this.showLogout = config.externalLogout;
  }

  async logout(): Promise<void> {
    await this.securityService.logoutUser();

    // If external auth with logout URL, redirect there
    const config = this.securityService.getConfig();
    if (this.securityService.isExternalAuth() && config.externalLogout && config.url) {
      window.location.href = config.url + '/logout';
    }
  }

  async tryAgain(): Promise<void> {
    // Clear stored token and re-authenticate
    this.securityService.storeToken();

    const config = this.securityService.getConfig();
    if (this.securityService.isExternalAuth() && config.url) {
      // Redirect to external auth
      window.location.href = config.url;
    } else {
      await this.router.navigate(['/']);
    }
  }
}
